/** An instance of the component. */
interface Resources {
  root: HTMLElement;
  typeFilters: HTMLElement[];
  itemsWrapper: HTMLElement;
  items: HTMLElement[];
  expandButton: HTMLElement | null;
}

const NUMBER_OF_ITEMS_WHEN_TRUNCATED = 11;

/** Name of the type of item filtered by a type filter. */
const typeFilterType = (typeFilter: HTMLElement): string | undefined =>
  typeFilter.dataset.type;

/** Change the active type filter. */
const setActiveTypeFilter = (resources: Resources, type: string): boolean => {
  resources.typeFilters.forEach((typeFilter) =>
    typeFilter.classList.toggle(
      "is-active",
      typeFilterType(typeFilter) === type
    )
  );

  return true;
};

/** Set the active type filter automatically. */
const applyDefaultTypeFilter = (resources: Resources): boolean => {
  if (resources.typeFilters.length > 0) {
    const defaultType = typeFilterType(resources.typeFilters[0]);

    if (defaultType === undefined) {
      return false;
    }

    setActiveTypeFilter(resources, defaultType);
  }

  return true;
};

/** Determine if a type filter is active. */
const isTypeFilterActive = (typeFilter: HTMLElement): boolean =>
  typeFilter.classList.contains("is-active");

/** Type of an item. */
const itemType = (item: HTMLElement): string | undefined => item.dataset.type;

/** Make an item visible. */
const showItem = (item: HTMLElement): HTMLElement => {
  item.classList.add("is-visible");

  return item;
};

/** Make an item hidden. */
const hideItem = (item: HTMLElement): HTMLElement => {
  item.classList.remove("is-visible");

  return item;
};

/** Update the shown items based on the currently active type filter. */
const filterItemsByType = (resources: Resources): boolean => {
  const activeFilter = resources.typeFilters.find(isTypeFilterActive);

  if (activeFilter === undefined) {
    return false;
  }

  resources.items
    .filter((item) => itemType(item) === typeFilterType(activeFilter))
    .map(showItem);

  resources.items
    .filter((item) => itemType(item) !== typeFilterType(activeFilter))
    .map(hideItem)
    .map((item) => resources.itemsWrapper.append(item));

  return true;
};

/** Listen for type filter click events. */
const attachTypeFilters = (resources: Resources): boolean => {
  resources.typeFilters.forEach((typeFilter) => {
    const type = typeFilterType(typeFilter);

    if (type === undefined) {
      return false;
    }

    typeFilter.addEventListener(
      "click",
      () =>
        setActiveTypeFilter(resources, type) &&
        filterItemsByType(resources) &&
        truncateItems(resources)
    );
  });

  return true;
};

/** Truncate the items shown. */
const truncateItems = (resources: Resources): boolean => {
  const activeFilter = resources.typeFilters.find(isTypeFilterActive);

  if (activeFilter === undefined) {
    return false;
  }

  const typeItems = resources.items.filter(
    (item) => itemType(item) === typeFilterType(activeFilter)
  );

  typeItems
    .slice(NUMBER_OF_ITEMS_WHEN_TRUNCATED)
    .map(hideItem)
    .map((item) => {
      resources.itemsWrapper.append(item);
    });

  if (typeItems.length > NUMBER_OF_ITEMS_WHEN_TRUNCATED) {
    resources.expandButton?.removeAttribute("disabled");
    resources.expandButton?.parentElement?.classList.remove("hidden");
    resources.root.classList.add("is-collapsed");
  } else {
    resources.expandButton?.setAttribute("disabled", "disabled");
    resources.expandButton?.parentElement?.classList.add("hidden");
    resources.root.classList.remove("is-collapsed");
  }

  return true;
};

/** Listen for expand click events. */
const attachExpand = (resources: Resources): boolean => {
  if (resources.expandButton === null) {
    return false;
  }

  resources.expandButton.addEventListener("click", () => {
    filterItemsByType(resources);
    resources.expandButton?.setAttribute("disabled", "disabled");
    resources.expandButton?.parentElement?.classList.add("hidden");
    resources.root.classList.remove("is-collapsed");
  });

  return true;
};

/** Every resources component. */
const resources: Resources[] = [
  ...document.querySelectorAll(".js-resources"),
].map((root): Resources => {
  return {
    root: root as HTMLElement,
    typeFilters: [
      ...root.querySelectorAll(".js-resources-type-filter"),
    ] as HTMLElement[],
    itemsWrapper: root.querySelector(
      ".js-resources-items-wrapper"
    ) as HTMLElement,
    items: [...root.querySelectorAll(".js-resources-item")] as HTMLElement[],
    expandButton: root.querySelector(".js-resources-expand"),
  };
});

/* Setup the resources. */
resources.forEach(applyDefaultTypeFilter);
resources.forEach(filterItemsByType);
resources.forEach(attachTypeFilters);
resources.forEach(truncateItems);
resources.forEach(attachExpand);
